import { useEditorRuntimeStore } from '../stores/useEditorRuntimeStore';
import { useEditorStore } from '../stores/useEditorStore';
import { point, type Point } from '../utils/math';

import { getConfig } from './config';
import { sortElementsByLayer, type GenericElement } from './element';
import { getPlugin } from './plugin';
import {
  getSelectionBoxShape,
  type LineSegment,
  type Polyline,
} from './shapes';

export const getElementAtPosition = (
  x: number,
  y: number,
): GenericElement | null => {
  const allHitElements = getElementsAtPosition(x, y);

  if (allHitElements.length > 1) {
    return allHitElements[allHitElements.length - 1];
  }

  if (allHitElements.length === 1) {
    return allHitElements[0];
  }

  return null;
};

export const getElementsAtPosition = (
  x: number,
  y: number,
): GenericElement[] => {
  const elements = Array.from(useEditorStore.getState().elements.values());

  return elements
    .filter((element) => hitElement(x, y, element))
    .toSorted(sortElementsByLayer);
};

export const getElementHitThreshold = () => {
  const config = getConfig();
  return (
    config.collision.threshold / useEditorRuntimeStore.getState().zoom.value
  );
};

export const hitElement = (x: number, y: number, element: GenericElement) => {
  if (!element.shape || !element.isVisible) return false;

  const state = useEditorStore.getState();

  if (element.renderedById) {
    const renderByElement = state.elements.get(element.renderedById);
    if (!renderByElement?.isVisible) return false;
  }

  const plugin = getPlugin(element.type);

  if (
    state.selectedElementIds.includes(element.id) &&
    !plugin.collision.onlyCountDirectElementHitAfterSelection
  ) {
    const selectionShape = getSelectionBoxShape(element);
    return pointInShape(
      point(x, y),
      selectionShape.data,
      getElementHitThreshold(),
    );
  }

  return (
    plugin.collision.isPointInShape(point(x, y), element) ||
    plugin.collision.isPointOnShape(
      point(x, y),
      element,
      getElementHitThreshold(),
    )
  );
};

export const pointOnShape = (
  point: Point,
  shape: string,
  lineWidth: number,
) => {
  const context = useEditorRuntimeStore
    .getState()
    .staticCanvas?.getContext('2d');

  if (!context) return false;

  const path = new Path2D(shape);
  context.lineWidth = lineWidth + getElementHitThreshold();

  return context.isPointInStroke(path, point[0], point[1]);
};

export const pointInShape = (
  point: Point,
  shape: string,
  lineWidth: number,
) => {
  const context = useEditorRuntimeStore
    .getState()
    .staticCanvas?.getContext('2d');

  if (!context) return false;

  const path = new Path2D(shape);
  context.lineWidth = lineWidth + getElementHitThreshold();

  return context.isPointInPath(path, point[0], point[1]);
};

// DEPRECATED
export const pointOnPolyline = (
  point: Point,
  polyline: Polyline,
  threshold?: number,
) => {
  return polyline.some((line) => pointOnLineSegment(point, line, threshold));
};

// DEPRECATED
export const pointOnLineSegment = (
  point: Point,
  line: LineSegment,
  threshold?: number,
) => {
  const config = getConfig();

  const distance = distanceToLineSegment(point, line);

  if (distance === 0) {
    return true;
  }

  return distance < (threshold ?? config.collision.threshold);
};

// DEPRECATED
export const distanceToLineSegment = (point: Point, line: LineSegment) => {
  const [x, y] = point;
  const [[x1, y1], [x2, y2]] = line;

  const A = x - x1;
  const B = y - y1;
  const C = x2 - x1;
  const D = y2 - y1;

  const dot = A * C + B * D;
  const len_sq = C * C + D * D;
  let param = -1;
  if (len_sq !== 0) {
    param = dot / len_sq;
  }

  let xx;
  let yy;

  if (param < 0) {
    xx = x1;
    yy = y1;
  } else if (param > 1) {
    xx = x2;
    yy = y2;
  } else {
    xx = x1 + param * C;
    yy = y1 + param * D;
  }

  const dx = x - xx;
  const dy = y - yy;
  return Math.sqrt(dx * dx + dy * dy);
};
